/**
 * capturedFetch — transparent fetch wrapper for the in-app debug overlay.
 *
 * When the debug flag is OFF:  pure passthrough — zero overhead; identical
 * to calling fetch() directly.
 *
 * When the debug flag is ON:  wraps the native fetch call, records
 * {method, url, status, duration} as an 'http' ring-buffer entry (IAC-9),
 * then returns the original Response (or re-throws the original error).
 *
 * Usage: replace call sites that should be observable with capturedFetch()
 * instead of fetch().  The wrapper is a transparent pass-through when the
 * flag is OFF, so there is zero production overhead unless the operator has
 * enabled the debug overlay.
 *
 * Isolation contract: this module does NOT import consoleCapture.ts.
 * The flag is read directly from debugTelemetryService.getSync() (same
 * source of truth as useDebugFlag).  This keeps the module independently
 * testable without side-effects on the console tee.
 */

import { debugTelemetryService } from '@/shared/services/debugTelemetryService';

import { ringBuffer } from './ringBuffer';
import { safeSerialize } from './safeSerialize';
import { store } from './store';

// ─── helpers ─────────────────────────────────────────────────────────────────

// Use Parameters<typeof fetch> to reference DOM types without spelling them out
// as literal identifiers (avoids ESLint no-undef false positives).
type FetchInput = Parameters<typeof fetch>[0];
type FetchInit  = Parameters<typeof fetch>[1];

function resolveUrl(input: FetchInput): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url; // Request.url
}

function resolveMethod(init: FetchInit): string {
  return (init?.method ?? 'GET').toUpperCase();
}

function pushHttpEntry(method: string, url: string, status: number, duration: number): void {
  ringBuffer.push({
    t: Date.now(),
    type: 'http',
    payload: safeSerialize({ method, url, status, duration }),
  });
  store.notify();
}

// ─── capturedFetch ────────────────────────────────────────────────────────────

/**
 * Drop-in replacement for `fetch` at monitored call sites.
 * Typed as `typeof fetch` so it accepts every valid fetch overload.
 */
export const capturedFetch: typeof fetch = async (input, init) => {
  // No-op passthrough when flag is OFF — identical cost to a bare function call.
  if (!debugTelemetryService.getSync()) {
    return fetch(input, init);
  }

  const url = resolveUrl(input);
  const method = resolveMethod(init);
  const start = performance.now();

  try {
    const response = await fetch(input, init);
    const duration = Math.round(performance.now() - start);
    pushHttpEntry(method, url, response.status, duration);
    return response;
  } catch (err) {
    const duration = Math.round(performance.now() - start);
    // status=0 indicates a network-level failure (no HTTP response available).
    pushHttpEntry(method, url, 0, duration);
    throw err;
  }
};
